/**
 * Narrated recording.
 *
 * The coach hits record, then works the board — dragging players, drawing with a
 * finger, walking the phases — while talking. When they stop, the video is
 * prepared (a progress bar) and is then ready to share: an MP4 every phone,
 * computer and messenger plays, smooth at 60 fps whatever the machine.
 *
 * The recording itself lives in `useRecording`; this panel shows it and drives
 * it, wherever the layout puts the panel. The coach's saved plays come under
 * it, as `children`.
 */

import type { JSX, ReactNode } from 'react';
import { formatClock, type RecordingApi } from './useRecording.ts';

interface RecordPanelProps {
  readonly recording: RecordingApi;
  /** Shown under the recording controls: the saved plays. */
  readonly children?: ReactNode;
}

export function RecordPanel({ recording, children }: RecordPanelProps): JSX.Element {
  const { phase, supported, secureContext, withAudio, elapsed, progress, result, resultUrl, notice, error } = recording;
  const isRecording = phase === 'recording';

  return (
    <section className="record">
      <div className="record__group" data-tutorial="recording">
        <div className="record__head">
          <h2 className="toolbar__heading">Record</h2>
          {isRecording && (
            <span className="record__live">
              <span className="record__dot" aria-hidden="true" /> {formatClock(elapsed)}
            </span>
          )}
        </div>

        {!supported && (
          <p className="record__error">This browser can’t make videos yet. Please update it to the latest version.</p>
        )}
        {supported && !secureContext && (
          <p className="record__error">
            Microphone access needs a secure context. Open the app on <code>localhost</code> or over HTTPS.
          </p>
        )}

        <label className="checkbox">
          <input
            type="checkbox"
            checked={withAudio}
            disabled={phase !== 'idle'}
            onChange={(event) => recording.setWithAudio(event.target.checked)}
          />
          <span>Record voice-over</span>
        </label>

        {phase === 'recording' && (
          <button type="button" className="btn btn--danger" onClick={() => void recording.stop()}>
            ■ Stop &amp; save
          </button>
        )}
        {phase === 'idle' && (
          <button type="button" className="btn btn--record" onClick={() => void recording.start()} disabled={!supported}>
            ● Start recording
          </button>
        )}
        {phase === 'preparing' && (
          <div className="record__preparing" role="status">
            <span>Preparing your video… {Math.round(progress * 100)}%</span>
            <progress max={1} value={progress} aria-label="Preparing your video" />
          </div>
        )}

        {notice && <p className="record__notice">{notice}</p>}
        {error && <p className="record__error">{error}</p>}
        {error && phase === 'idle' && recording.canRetry && (
          <button type="button" className="btn btn--primary" onClick={() => void recording.retry()}>
            Try again
          </button>
        )}

        {result && resultUrl && (
          <div className="record__result">
            <video className="record__preview" src={resultUrl} controls playsInline />
            <div className="record__meta">
              {formatClock(result.durationMs)} · {result.extension.toUpperCase()} ·{' '}
              {result.hasAudio ? 'with voice-over' : 'silent'} · {(result.blob.size / 1_000_000).toFixed(1)} MB
            </div>
            <button type="button" className="btn btn--primary" onClick={() => void recording.save()}>
              Download / share
            </button>
          </div>
        )}
      </div>
      {children}
    </section>
  );
}
